import { useEffect, useState } from "react";
import type React from "react";
import { useTranslation } from "react-i18next";

export default function HeroSection() {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const fadeStyle = (delay: number): React.CSSProperties => ({
    transitionDelay: mounted ? `${delay}ms` : "0ms",
  });

  const fadeClass = `transition-all duration-700 ease-out ${
    mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
  }`;

  const pills = [
    { icon: "ri-close-circle-line", key: "hero_pill1" },
    { icon: "ri-wifi-line", key: "hero_pill2" },
    { icon: "ri-calendar-check-line", key: "hero_pill3" },
    { icon: "ri-group-line", key: "hero_pill4" },
  ];

  return (
    <section
      id="home"
      className="relative w-full min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Okinawa%20tropical%20island%20distant%20aerial%20view%20showing%20entire%20island%20small%20in%20frame%20surrounded%20by%20vast%20dark%20turquoise%20ocean%20island%20positioned%20right%20side%20but%20fully%20visible%20wide%20open%20sea%20left%20side%20for%20text%20empty%20space%20morning%20light%20mist%20cinematic%20wide%20angle%20composition%20island%20clearly%20separated%20from%20text%20area&width=1440&height=900&seq=hero010&orientation=landscape"
          alt="沖縄の離島・伊計島の空撮写真 - てんてんおじさんが鑑定を行う沖縄の海"
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchPriority="high"
          decoding="sync"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-16">
        <div className="max-w-2xl">
          {/* Headline */}
          <h1
            className={`text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-snug mb-4 sm:mb-6 ${fadeClass}`}
            style={{ ...{ fontFamily: "'Noto Serif JP', serif" }, ...fadeStyle(100) }}
          >
            {t("hero_headline1")}<br />
            {t("hero_headline2")}<br />
            <span className="text-[#0ABAB5]">{t("hero_headline3")}</span><br />
            {t("hero_headline4")}
          </h1>

          {/* Body copy */}
          <p
            className={`text-white/80 text-xs sm:text-sm md:text-lg leading-loose mb-6 sm:mb-8 max-w-xl ${fadeClass}`}
            style={fadeStyle(300)}
          >
            {t("hero_body").split("\n").map((line, i) => (
              <span key={i}>{line}{i < t("hero_body").split("\n").length - 1 && <br />}</span>
            ))}
          </p>

          {/* Feature pills — モバイルで読みやすく */}
          <div className={`flex flex-wrap gap-2 mb-7 sm:mb-10 ${fadeClass}`} style={fadeStyle(500)}>
            {pills.map((f) => (
              <div
                key={f.key}
                className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs font-medium px-3 py-2 rounded-full"
              >
                <i className={`${f.icon} text-[#0ABAB5] text-sm flex-shrink-0`} />
                <span className="leading-tight">{t(f.key)}</span>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div className={`flex flex-col sm:flex-row gap-3 ${fadeClass}`} style={fadeStyle(700)}>
            <button
              onClick={() => handleNav("#contact")}
              className="flex items-center justify-center gap-2 bg-[#0ABAB5] text-white font-semibold px-6 sm:px-8 py-3.5 rounded-full hover:bg-[#089e99] transition-all duration-200 text-sm sm:text-base whitespace-nowrap cursor-pointer"
            >
              {t("nav_cta")}
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => handleNav("#services")}
              className="flex items-center justify-center gap-2 border-2 border-white/70 text-white font-semibold px-6 sm:px-8 py-3.5 rounded-full hover:bg-white/10 transition-all duration-200 text-sm sm:text-base whitespace-nowrap cursor-pointer"
            >
              {t("hero_btn_services")}
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 animate-bounce">
        <span className="text-xs tracking-widest">{t("hero_scroll")}</span>
        <i className="ri-arrow-down-line text-lg" />
      </div>
    </section>
  );
}
